/**
 * Marketing i18n fragment — integrationsMatrix (/integrations status matrix).
 *
 * Display copy for the integration-status matrix on the /integrations hub.
 * Structure (entries, statuses, hrefs, guard tokens) lives in
 * src/lib/marketing-integrations.ts; this file holds only the words. Every
 * claim follows docs/ai/fixcontrol-website-claims.md: shipped behaviour only,
 * "where configured" stated as such, planned items always carry roadmap
 * wording.
 */

export const integrationsMatrixEn = {
  eyebrow: "Status matrix",
  title: "Every integration, with its real maturity",
  description:
    "What each connection can do, what it needs approval for, which plan carries it, and where its limits are. If something says planned, it is not sold as shipped.",
  statuses: {
    live: "Live",
    configured: "Live where configured",
    preview: "Preview",
    planned: "Planned",
  },
  fields: {
    actions: "Supported actions",
    approval: "Approvals & visibility",
    plan: "Plan",
    limits: "Limitations",
  },
  items: {
    slack: {
      actions: "Intake, ticket-question answering, approval decision cards and notifications inside your workspace.",
      approval: "Internal, operator-facing surface. Chat approvals require a verified, elevated Slack identity.",
      plan: "All plans",
      limits: "Not a customer-visible reply channel — customer replies go out via your helpdesk.",
    },
    teams: {
      actions: "Intake, ticket-question answering, approval cards (Adaptive Cards) and gate decisions.",
      approval: "Internal, operator-facing surface. Chat approvals require a verified, elevated Teams identity.",
      plan: "Team and above",
      limits: "Mirroring run progress back into Teams threads is planned; some surfaces are newer than their Slack equivalents.",
    },
    freshdesk: {
      actions: "Two-way ticket sync, AI-drafted replies, status and priority updates, progress notes.",
      approval: "Customer-visible. Every reply passes reply-safety checks and a recorded human approval before send.",
      plan: "Starter (as your one helpdesk) or Team and above",
      limits: "Replies send from your own support address through the governed outbox — retry and dead-letter included.",
    },
    jira: {
      actions: "Issue sync, internal comments, transitions, and an inbound status-sync webhook.",
      approval: "Internal-only: FixControl posts internal comments, never customer-visible content, on Jira Software issues.",
      plan: "Starter (as your one tracker) or Team and above",
      limits: "Customer-visible replies require Jira Service Management (separate row).",
    },
    jsm: {
      actions: "Customer-visible replies on Jira Service Management customer requests.",
      approval: "Four gates, all required: a configured request type, a healthy customer-reply capability, a reply-safety pass, and a recorded human approval.",
      plan: "Team and above",
      limits: "Only where configured; when any gate fails the reply falls back to an internal comment.",
    },
    odoo: {
      actions: "Ticket create and link, two-way status sync, AI-drafted replies, internal progress notes, and an inbound webhook; resolved Odoo tickets serve as evidence for support answers.",
      approval: "Customer-visible. Every reply passes reply-safety checks and a recorded human approval before send.",
      plan: "Starter (as your one helpdesk) or Team and above",
      limits: "Customer replies land in the ticket's conversation through the governed outbox — retry and dead-letter included; internal notes stay internal.",
    },
    topdesk: {
      actions: "Incident create and link, status read-back, AI-drafted replies, internal progress notes with pull-request links, and an inbound webhook; the caller on the incident links the customer, and resolved TOPdesk incidents serve as evidence for support answers.",
      approval: "Customer-visible. Every reply passes reply-safety checks and a recorded human approval before send; internal notes stay invisible to the caller.",
      plan: "Starter (as your one helpdesk) or Team and above",
      limits: "Inbound updates come from an Events & Actions HTTP action you configure in TOPdesk, and FixControl reads the incident back over the API before acting on it. Progress-note sync, status push on resolve and the customer reply are separate opt-in toggles, off by default. Attachments are not synced.",
    },
    afas: {
      actions: "Read-only HR data for governed answers: remaining leave hours with an as-of date and a payroll explanation code, read from GetConnector views you define with prescribed field ids.",
      approval: "Nothing is written back. An answer about an employee's own situation is a draft until an assigned reviewer and a second approver decide on it.",
      plan: "Confirmed during onboarding",
      limits: "Every call filters on one person and projects onto the named fields — never a salary amount or a payslip document. One connection check per connection; a missing employee record routes that one question to a person instead of colouring the connection red. Credentials and the projects that may use them are set up with us; there is no self-service connection form yet.",
    },
    youforce: {
      actions: "Read-only HR data for governed answers: remaining leave hours with an as-of date and a payroll explanation code, read from the Youforce domain-API endpoints you expose.",
      approval: "Nothing is written back. An answer about an employee's own situation is a draft until an assigned reviewer and a second approver decide on it.",
      plan: "Confirmed during onboarding",
      limits: "Endpoint templates must stay on the base URL's host over https, or the request is refused before it is made. The connection check forces a token refresh, so it proves the credential and not that every endpoint template resolves. Credentials and the projects that may use them are set up with us; there is no self-service connection form yet.",
    },
    github: {
      actions: "Repository analysis, pull-request delivery of approved changes, PR comments, webhook events.",
      approval: "Pull-request-only delivery — merging stays a human action in your review flow.",
      plan: "Pro and above",
      limits: "Never pushes to a default branch.",
    },
    gitlab: {
      actions: "Repository analysis, merge-request delivery of approved changes, MR comments, webhook events.",
      approval: "Merge-request-only delivery — merging stays a human action in your review flow.",
      plan: "Pro and above",
      limits: "Never pushes to a default branch.",
    },
    bitbucket: {
      actions: "Cloud and Server/Data Center: repository analysis, pull-request delivery, comments, webhook events.",
      approval: "Pull-request-only delivery, behind the same approval gates as GitHub and GitLab.",
      plan: "Confirmed during onboarding",
      limits: "No dedicated marketing page yet, and health probing is more basic than for GitHub/GitLab.",
    },
    linear: {
      actions: "Issue sync, internal comments and transitions (GraphQL; OAuth or API key).",
      approval: "Internal comments only — no customer-visible replies. Writes go through the outbox and approval flow.",
      plan: "Confirmed during onboarding",
      limits: "Inbound webhook sync is on the roadmap.",
    },
    email: {
      actions: "Inbound mailboxes (Gmail, Office 365) become Inbox items; governed outbound notifications and portable approval emails.",
      approval: "Customer-facing email replies follow comms governance; an optional low-risk auto-send mode is opt-in, policy-bounded and audit-logged.",
      plan: "Confirmed during onboarding",
      limits: "Mailbox connections are per-tenant OAuth; re-consent is surfaced on the settings tile.",
    },
    websiteChat: {
      actions: "Public intake widget: lead capture, answers from public knowledge, and project-scoped ticket status in customer-safe form.",
      approval: "Every outgoing message passes a public reply-safety wall; origin allowlist and rate limits are fail-closed.",
      plan: "All plans",
      limits: "It cannot reach internal notes, internal ticket data or any other organization's data — it sees only the bound project's customer-safe view.",
    },
    kubernetesArgo: {
      actions: "Deployment visibility plus Argo Rollouts promotion gates: an approval resumes a paused rollout; abort and read-status where configured. Private clusters connect through the FC Agent — outbound only.",
      approval: "Nothing promotes without a recorded human decision.",
      plan: "Pro (where configured) / Enterprise",
      limits: "No general cluster write access — the closed set of gated operations is the only execution path.",
    },
    fcAgent: {
      actions: "A small service in your own environment that connects outbound to FixControl over HTTPS and executes signed, expiring, single-use operations on Kubernetes, Argo, Jenkins and GitLab — so none of them needs to be publicly exposed.",
      approval: "Every execution verdict is a recorded human decision; the agent verifies signature, expiry and your local allowlists before executing any operation.",
      plan: "Pilot onboarding (where configured)",
      limits: "Outbound-only by design: no inbound access, no arbitrary commands — a closed operation vocabulary with least-privilege RBAC on your side.",
    },
    cicdGates: {
      actions: "Deployment gates on pauses your pipeline already holds: GitHub Actions environment reviews, GitLab deployment approvals and manual jobs, Jenkins input steps. The verdict executes through the host's own primitive — directly or via the FC Agent.",
      approval: "FixControl never starts a deployment — it records a human decision on an existing pause and carries out exactly that verdict.",
      plan: "Pilot onboarding (where configured, per-tenant opt-in)",
      limits: "Gates open only on a pause the host reports; providers without a pause primitive stay observe-only.",
    },
    database: {
      actions: "Read-only diagnostics against your PostgreSQL database, with sensitive column names redacted before results reach the AI.",
      approval: "Every query shape is allowlisted and approval-gated; read-only access is verified before first use.",
      plan: "Confirmed during onboarding",
      limits: "PostgreSQL only. Queries can inspect data but cannot change it.",
    },
    gcp: {
      actions: "Read-only Cloud Logging and Error Reporting as diagnostic context on a bug report. BigQuery access is configured and health-checked, but nothing queries it automatically yet.",
      approval: "Read-only access only. You allowlist the log filters and datasets; secrets are masked before anything reaches the AI, and health is reported per capability.",
      plan: "Confirmed during onboarding",
      limits: "Read-only — nothing is ever written to your Google Cloud project. A BigQuery query is refused before it runs when it would exceed the cost ceiling you set.",
    },
    api: {
      actions: "Public REST API (issues, patches, webhooks, integration status) with a published OpenAPI spec; signed outbound webhooks.",
      approval: "Tenant-scoped API keys; outbound webhooks carry timestamped HMAC-SHA256 signatures.",
      plan: "Confirmed during onboarding",
      limits: "Rate-limited per key.",
    },
    devops: {
      actions: "Observe-and-govern preview: pipeline and deployment event ingest (also Azure DevOps, Vercel, Terraform Cloud) with compliance export.",
      approval: "Observe-only for these sources — it scores and records deployments; it does not execute them. Deployment gates are the separate CI/CD deployment gates row.",
      plan: "Preview — scoped during pilot",
      limits: "Labelled preview: coverage and surfaces are still maturing.",
    },
    whatsapp: {
      actions: "On the roadmap — WhatsApp intake is not available yet.",
      approval: "—",
      plan: "—",
      limits: "Not connected or configurable today; tell us if it matters for your evaluation.",
    },
    saml: {
      actions: "On the roadmap — SAML SSO is not shipped; OIDC SSO and SCIM 2.0 are the shipped identity integrations.",
      approval: "—",
      plan: "—",
      limits: "SAML stays on the roadmap for now — ask us about timelines if it is a hard requirement.",
    },
  },
};

export const integrationsMatrixNl: typeof integrationsMatrixEn = {
  eyebrow: "Statusmatrix",
  title: "Elke integratie, met haar echte volwassenheid",
  description:
    "Wat elke koppeling kan, waarvoor ze goedkeuring nodig heeft, welk plan haar draagt en waar de grenzen liggen. Staat er gepland, dan verkopen we het niet als geleverd.",
  statuses: {
    live: "Live",
    configured: "Live waar geconfigureerd",
    preview: "Preview",
    planned: "Gepland",
  },
  fields: {
    actions: "Ondersteunde acties",
    approval: "Goedkeuringen & zichtbaarheid",
    plan: "Plan",
    limits: "Beperkingen",
  },
  items: {
    slack: {
      actions: "Intake, ticketvragen beantwoorden, goedkeuringskaarten en meldingen in je eigen workspace.",
      approval: "Interne, operator-gerichte laag. Chat-goedkeuringen vereisen een geverifieerde, verhoogde Slack-identiteit.",
      plan: "Alle plannen",
      limits: "Geen klantzichtbaar antwoordkanaal — klantantwoorden gaan via je helpdesk naar buiten.",
    },
    teams: {
      actions: "Intake, ticketvragen beantwoorden, goedkeuringskaarten (Adaptive Cards) en gate-beslissingen.",
      approval: "Interne, operator-gerichte laag. Chat-goedkeuringen vereisen een geverifieerde, verhoogde Teams-identiteit.",
      plan: "Team en hoger",
      limits: "Voortgang terugspiegelen in Teams-threads is gepland; sommige onderdelen zijn nieuwer dan hun Slack-equivalent.",
    },
    freshdesk: {
      actions: "Twee-weg ticketsync, AI-opgestelde antwoorden, status- en prioriteitsupdates, voortgangsnotities.",
      approval: "Klantzichtbaar. Elk antwoord passeert reply-safety-checks en een vastgelegde menselijke goedkeuring vóór verzending.",
      plan: "Starter (als je ene helpdesk) of Team en hoger",
      limits: "Antwoorden vertrekken vanaf je eigen supportadres via de governed outbox — met retry en dead-letter.",
    },
    jira: {
      actions: "Issue-sync, interne comments, transitions en een inkomende status-sync-webhook.",
      approval: "Alleen intern: FixControl plaatst interne comments, nooit klantzichtbare content, op Jira Software-issues.",
      plan: "Starter (als je ene tracker) of Team en hoger",
      limits: "Klantzichtbare antwoorden vereisen Jira Service Management (aparte rij).",
    },
    jsm: {
      actions: "Klantzichtbare antwoorden op Jira Service Management-klantverzoeken.",
      approval: "Vier poorten, allemaal vereist: een geconfigureerd request type, een gezonde customer-reply-capability, een reply-safety-pass en een vastgelegde menselijke goedkeuring.",
      plan: "Team en hoger",
      limits: "Alleen waar geconfigureerd; faalt een poort, dan valt het antwoord terug op een interne comment.",
    },
    odoo: {
      actions: "Tickets aanmaken en koppelen, twee-weg statussync, AI-opgestelde antwoorden, interne voortgangsnotities en een inkomende webhook; opgeloste Odoo-tickets dienen als bewijs voor supportantwoorden.",
      approval: "Klantzichtbaar. Elk antwoord passeert reply-safety-checks en een vastgelegde menselijke goedkeuring vóór verzending.",
      plan: "Starter (als je ene helpdesk) of Team en hoger",
      limits: "Klantantwoorden landen in de conversatie van het ticket via de governed outbox — met retry en dead-letter; interne notities blijven intern.",
    },
    topdesk: {
      actions: "Incidenten aanmaken en koppelen, status teruglezen, AI-opgestelde antwoorden, interne voortgangsnotities met pull-request-links en een inkomende webhook; de caller op het incident koppelt de klant, en opgeloste TOPdesk-incidenten dienen als bewijs voor supportantwoorden.",
      approval: "Klantzichtbaar. Elk antwoord passeert reply-safety-checks en een vastgelegde menselijke goedkeuring vóór verzending; interne notities blijven onzichtbaar voor de caller.",
      plan: "Starter (als je ene helpdesk) of Team en hoger",
      limits: "Inkomende updates komen van een Events & Actions HTTP-action die je zelf in TOPdesk instelt, en FixControl leest het incident via de API terug voordat het ernaar handelt. Voortgangsnotitie-sync, statuspush bij oplossen en het klantantwoord zijn losse opt-in-toggles, standaard uit. Bijlagen worden niet gesynchroniseerd.",
    },
    afas: {
      actions: "Alleen-lezen HR-gegevens voor gegoverneerde antwoorden: resterende verlofuren met peildatum en een looncode, gelezen uit GetConnector-overzichten die je zelf inricht met voorgeschreven veld-id's.",
      approval: "Er wordt niets teruggeschreven. Een antwoord over de eigen situatie van een medewerker blijft concept tot een toegewezen behandelaar en een tweede goedkeurder erover beslissen.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Elke aanroep filtert op één persoon en projecteert op de benoemde velden — nooit een salarisbedrag of een loonstrookdocument. Eén verbindingscontrole per koppeling; een ontbrekend medewerkerrecord stuurt die ene vraag naar een mens in plaats van de koppeling voor iedereen rood te kleuren. Inloggegevens en de projecten die ze mogen gebruiken richten we samen met je in; een zelfbedieningsformulier is er nog niet.",
    },
    youforce: {
      actions: "Alleen-lezen HR-gegevens voor gegoverneerde antwoorden: resterende verlofuren met peildatum en een looncode, gelezen uit de Youforce-domein-API-endpoints die je zelf aanbiedt.",
      approval: "Er wordt niets teruggeschreven. Een antwoord over de eigen situatie van een medewerker blijft concept tot een toegewezen behandelaar en een tweede goedkeurder erover beslissen.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Endpoint-sjablonen moeten op de host van de basis-URL blijven en via https lopen, anders wordt het verzoek geweigerd voordat het uitgaat. De verbindingscontrole dwingt een tokenvernieuwing af en bewijst daarmee de inloggegevens, niet dat elk sjabloon oplost. Inloggegevens en de projecten die ze mogen gebruiken richten we samen met je in; een zelfbedieningsformulier is er nog niet.",
    },
    github: {
      actions: "Repository-analyse, oplevering van goedgekeurde wijzigingen als pull request, PR-comments, webhook-events.",
      approval: "Alleen pull-request-oplevering — mergen blijft een menselijke actie in jouw reviewflow.",
      plan: "Pro en hoger",
      limits: "Pusht nooit naar een default branch.",
    },
    gitlab: {
      actions: "Repository-analyse, oplevering van goedgekeurde wijzigingen als merge request, MR-comments, webhook-events.",
      approval: "Alleen merge-request-oplevering — mergen blijft een menselijke actie in jouw reviewflow.",
      plan: "Pro en hoger",
      limits: "Pusht nooit naar een default branch.",
    },
    bitbucket: {
      actions: "Cloud en Server/Data Center: repository-analyse, pull-request-oplevering, comments, webhook-events.",
      approval: "Alleen pull-request-oplevering, achter dezelfde goedkeuringspoorten als GitHub en GitLab.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Nog geen eigen marketingpagina, en health-probing is eenvoudiger dan bij GitHub/GitLab.",
    },
    linear: {
      actions: "Issue-sync, interne comments en transitions (GraphQL; OAuth of API-key).",
      approval: "Alleen interne comments — geen klantzichtbare antwoorden. Writes lopen via de outbox en de goedkeuringsflow.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Inkomende webhook-sync staat op de roadmap.",
    },
    email: {
      actions: "Inkomende mailboxen (Gmail, Office 365) worden Inbox-items; governed uitgaande meldingen en portable goedkeurings-e-mails.",
      approval: "Klantgerichte e-mailantwoorden volgen comms-governance; een optionele low-risk auto-send-modus is opt-in, beleidsbegrensd en audit-gelogd.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Mailboxkoppelingen zijn per-tenant OAuth; re-consent verschijnt op de settings-tile.",
    },
    websiteChat: {
      actions: "Publieke intake-widget: leads vastleggen, antwoorden uit publieke kennis, en projectgebonden ticketstatus in klantveilige vorm.",
      approval: "Elk uitgaand bericht passeert een publieke reply-safety-wall; origin-allowlist en rate limits zijn fail-closed.",
      plan: "Alle plannen",
      limits: "Kan niet bij interne notities, interne ticketdata of de gegevens van een andere organisatie — ziet alleen de klantveilige weergave van het gekoppelde project.",
    },
    kubernetesArgo: {
      actions: "Deployment-zichtbaarheid plus Argo Rollouts-promotiepoorten: een goedkeuring hervat een gepauzeerde rollout; abort en status lezen waar geconfigureerd. Private clusters koppelen via de FC Agent — alleen uitgaand.",
      approval: "Niets promoveert zonder een vastgelegde menselijke beslissing.",
      plan: "Pro (waar geconfigureerd) / Enterprise",
      limits: "Geen algemene cluster-schrijftoegang — de gesloten set gegate operaties is het enige uitvoeringspad.",
    },
    fcAgent: {
      actions: "Een kleine service in je eigen omgeving die uitgaand met FixControl verbindt over HTTPS en getekende, aflopende, single-use operaties uitvoert op Kubernetes, Argo, Jenkins en GitLab — zodat geen van deze systemen publiek bereikbaar hoeft te zijn.",
      approval: "Elk uitvoerings-verdict is een vastgelegde menselijke beslissing; de agent verifieert handtekening, vervaltijd en je lokale allowlists vóór uitvoering van elke operatie.",
      plan: "Pilot-onboarding (waar geconfigureerd)",
      limits: "Alleen uitgaand by design: geen inkomende toegang, geen willekeurige commando's — een gesloten operatie-vocabulaire met least-privilege RBAC aan jouw kant.",
    },
    cicdGates: {
      actions: "Deployment-poorten op pauzes die je pipeline al vasthoudt: GitHub Actions environment reviews, GitLab-deployment-approvals en handmatige jobs, Jenkins-input-stappen. Het verdict wordt uitgevoerd via het eigen primitief van de host — direct of via de FC Agent.",
      approval: "FixControl start nooit een deployment — het legt een menselijke beslissing op een bestaande pauze vast en voert precies dat verdict uit.",
      plan: "Pilot-onboarding (waar geconfigureerd, opt-in per tenant)",
      limits: "Poorten openen alleen op een pauze die de host meldt; providers zonder pauze-primitief blijven observe-only.",
    },
    database: {
      actions: "Read-only diagnostiek op je PostgreSQL-database, met redactie van gevoelige kolomnamen voordat resultaten de AI bereiken.",
      approval: "Elke query-vorm is allowlisted en goedkeuringsgebonden; read-only-toegang wordt vóór eerste gebruik geverifieerd.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Alleen PostgreSQL. Query's kunnen data inzien maar niet wijzigen.",
    },
    gcp: {
      actions: "Read-only Cloud Logging en Error Reporting als diagnostische context bij een bugmelding. BigQuery-toegang wordt ingericht en gecontroleerd, maar nog niets bevraagt het automatisch.",
      approval: "Alleen leestoegang. Jij bepaalt de allowlist van logfilters en datasets; secrets worden gemaskeerd voordat iets de AI bereikt, en de gezondheid wordt per capability gemeld.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Alleen-lezen — er wordt nooit iets naar je Google Cloud-project geschreven. Een BigQuery-query wordt vóór uitvoering geweigerd als hij boven het kostenplafond komt dat je instelt.",
    },
    api: {
      actions: "Publieke REST-API (issues, patches, webhooks, integratiestatus) met gepubliceerde OpenAPI-spec; ondertekende uitgaande webhooks.",
      approval: "Tenant-gebonden API-keys; uitgaande webhooks dragen HMAC-SHA256-handtekeningen met tijdstempel.",
      plan: "Bevestigd tijdens onboarding",
      limits: "Rate-limited per key.",
    },
    devops: {
      actions: "Observe-and-govern-preview: inname van pipeline- en deployment-events (ook Azure DevOps, Vercel, Terraform Cloud) met compliance-export.",
      approval: "Alleen observeren voor deze bronnen — het scoort en registreert deployments; het voert ze niet uit. Deployment-poorten zijn de aparte rij CI/CD-deployment-poorten.",
      plan: "Preview — afgebakend tijdens de pilot",
      limits: "Gelabeld als preview: dekking en schermen zijn nog in ontwikkeling.",
    },
    whatsapp: {
      actions: "Op de roadmap — WhatsApp-intake is nog niet beschikbaar.",
      approval: "—",
      plan: "—",
      limits: "Vandaag niet te koppelen of te configureren; laat het weten als dit belangrijk is voor je evaluatie.",
    },
    saml: {
      actions: "Op de roadmap — SAML SSO is niet geleverd; OIDC SSO en SCIM 2.0 zijn de geleverde identity-integraties.",
      approval: "—",
      plan: "—",
      limits: "SAML blijft voorlopig op de roadmap — vraag ons naar tijdlijnen als het een harde eis is.",
    },
  },
};
